import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";

const ROTATING = [
  "AGENTIC AI",
  "MULTI-AGENT SYSTEMS",
  "FINTECH ML",
  "RAG + LLMs",
  "MLOps IN PROD"
];

export default function Hero({ profile }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % ROTATING.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative pt-28 pb-28 sm:pt-40 sm:pb-32 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-volt/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-neon-cyan/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="overline text-volt mb-6"
        >
          [ SYSTEM INITIALIZED · {profile?.location || "CONNECTICUT, USA"} ]
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-black tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-[8.5rem]"
          data-testid="hero-title"
        >
          {(profile?.hero_headline || "AGENTIC AI & FINTECH ENGINEERING.")
            .split(" ")
            .map((w, idx) => (
              <span key={idx} className={idx === 1 ? "text-volt" : ""}>
                {w}{" "}
              </span>
            ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 grid md:grid-cols-2 gap-10 items-end"
        >
          <div className="terminal max-w-xl">
            <div className="terminal-header">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot amber"></span>
              <span className="terminal-dot green"></span>
              <span className="ml-2 uppercase">~/skp --focus</span>
            </div>
            <div className="p-5 text-sm leading-7">
              <span className="text-fog-300">&gt; agent.role()</span>
              <div className="text-fog-100 mt-1">{profile?.title || "FinTech · MLOps · Agentic AI Engineer"}</div>
              <span className="text-fog-300 block mt-4">&gt; agent.focus()</span>
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-volt mt-1 font-bold"
                data-testid="hero-rotating-focus"
              >
                {ROTATING[i]}<span className="blink"></span>
              </motion.div>
              <span className="text-fog-300 block mt-4">&gt; agent.experience()</span>
              <div className="text-fog-100 mt-1">{profile?.years_experience || 7} years · production ML systems</div>
            </div>
          </div>

          <div>
            <p className="text-fog-200 text-lg leading-relaxed max-w-lg mb-8">
              {profile?.tagline || "Engineering agentic systems at the intersection of finance and machine learning."}
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#projects" className="btn-volt" data-testid="hero-cta-projects">
                View Projects <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`${BACKEND_URL}/api/resume`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid="hero-cta-resume"
              >
                <Download className="w-4 h-4" /> Resume
              </a>
              <a href="#contact" className="btn-ghost" data-testid="hero-cta-contact">
                Contact
              </a>
            </div>
            <div className="mt-10 flex gap-10">
              <Stat label="Years" value={profile?.years_experience || 7} />
              <Stat label="Projects" value="20+" />
              <Stat label="Certs" value="29" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-black text-4xl text-volt">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
